import * as React from "react";
import { t } from "../../../i18n";
import { CalculationBasis, CashbackTypeV2, GameTag } from "../../../http/protocol";
import {
  blockedFractionMessage,
  blockingFractionLimit,
  CashbackFractionThresholds,
  isLargeFraction,
  largeFractionThreshold,
} from "../cashbackFractionThresholds";
import { GameTagPillSelector } from "./GameTagPillSelector";

export interface LevelOverride {
  /** Stable React list key — never persisted. */
  rowId: string;
  level: string;
  fraction: string;
  // When true, this level takes its fraction from the level below rather than its own value.
  fractionInherited: boolean;
  // When true, this level takes its game-tag filtering from the level below; its own tag lists are ignored.
  gameTagsInherited: boolean;
  includeTags: GameTag[];
  excludeTags: GameTag[];
}

/** The level whose entry supplies the config-wide defaults; it is mandatory and cannot be removed. */
export const DEFAULT_LEVEL = "0";

let nextRowId = 0;

export function createLevelOverride(
  level: string,
  fraction: string,
  gameTagsInherited = false,
  includeTags: GameTag[] = [],
  excludeTags: GameTag[] = [],
  fractionInherited = false,
): LevelOverride {
  nextRowId += 1;
  return { rowId: `level-override-${nextRowId}`, level, fraction, fractionInherited, gameTagsInherited, includeTags, excludeTags };
}

interface Props {
  overrides: LevelOverride[];
  onChange: (overrides: LevelOverride[]) => void;
  // The advisory threshold depends on the config's basis, so an override fraction is judged against
  // the same basis its parent config pays out on.
  basis: CalculationBasis;
  // Operator-configured warning limits (Admin Security). Undefined until loaded — no warning shown.
  thresholds: CashbackFractionThresholds | undefined;
  // Tags the include/exclude selectors offer, shown only when a row does not inherit its game tags.
  availableTags: GameTag[];
  // Selects which limit set (rakeback vs cashback) applies; an unset type falls back to the
  // permissive cashback set.
  rewardType?: CashbackTypeV2;
  // Override rows a blocked save attempt flagged; the hard-stop message renders on each of them.
  blockedRowIds?: string[];
}

export const V2UserLevelsEditor: React.FC<Props> = ({ overrides, onChange, basis, thresholds, availableTags, rewardType, blockedRowIds = [] }) => {
  // Rows whose game-tag section is collapsed. Absence means expanded, so existing configs' tags show on load.
  const [collapsedRows, setCollapsedRows] = React.useState<Set<string>>(new Set());

  const toggleGameTagsCollapsed = (rowId: string): void => {
    setCollapsedRows((previous) => {
      const next = new Set(previous);
      if (next.has(rowId)) {
        next.delete(rowId);
      } else {
        next.add(rowId);
      }
      return next;
    });
  };

  const updateRow = (rowId: string, patch: Partial<LevelOverride>): void => {
    onChange(overrides.map((row) => (row.rowId === rowId ? { ...row, ...patch } : row)));
  };

  const addRow = (): void => {
    // A new override level usually adjusts its fraction while keeping the game tags from below, so
    // game tags start inherited.
    onChange([...overrides, createLevelOverride("", "", true)]);
  };

  // Level 0 carries the config-wide defaults, so it stays present regardless of a remove attempt.
  const removeRow = (rowId: string): void => {
    onChange(overrides.filter((row) => row.rowId !== rowId || row.level === DEFAULT_LEVEL));
  };

  // The fraction a level actually resolves to when it inherits: the nearest lower level that sets its
  // own fraction. Level 0 always sets its own, so it is the guaranteed floor.
  const inheritedFractionFor = (target: LevelOverride): string => {
    const targetLevel = Number(target.level);
    const nearestLower = overrides
      .filter((row) => row.rowId !== target.rowId && Number(row.level) < targetLevel && (!row.fractionInherited || row.level === DEFAULT_LEVEL))
      .sort((a, b) => Number(b.level) - Number(a.level))[0];
    if (nearestLower) {
      return nearestLower.fraction;
    }
    return overrides.find((row) => row.level === DEFAULT_LEVEL)?.fraction ?? "";
  };

  // The include/exclude tags a level would inherit: the nearest lower level that sets its own game
  // tags (level 0 is always such a level). Used to pre-populate a level's lists when it stops
  // inheriting, so the operator edits from the effective set rather than an empty one.
  const inheritedTagsFor = (target: LevelOverride): { includeTags: GameTag[]; excludeTags: GameTag[] } => {
    const targetLevel = Number(target.level);
    const nearestLower = overrides
      .filter((row) => row.rowId !== target.rowId && Number(row.level) < targetLevel && (!row.gameTagsInherited || row.level === DEFAULT_LEVEL))
      .sort((a, b) => Number(b.level) - Number(a.level))[0];
    const source = nearestLower ?? overrides.find((row) => row.level === DEFAULT_LEVEL);
    return { includeTags: source?.includeTags ?? [], excludeTags: source?.excludeTags ?? [] };
  };

  return (
    <div className="card-box">
      <div className="ibox-content">
        <h4 className="ibox-title">{t("cashback.config-v2.user-levels-title", undefined, "User Levels")}</h4>
        <span className="help">
          {t(
            "cashback.config-v2.user-levels-help",
            undefined,
            "Level 0 holds the default fraction and game tag inclusions/exclusions. Each higher level applies from that level upward until a higher level overrides it. Tick a level's inherit box to take its fraction or game tags from the level below instead of setting them here.",
          )}
          <br />
          <strong>
            {t("cashback.config-v2.user-levels-help-example", undefined, "Example: level 0 → 0.05, level 5 → 0.10. Levels 0–4 earn 0.05, levels 5+ earn 0.10.")}
          </strong>
        </span>
        <div className="hr-line-dashed"></div>

        {overrides.map((row, index) => {
          const isDefaultLevel = row.level === DEFAULT_LEVEL;
          // Level 0 is the floor every level inherits from — it has nothing below it, so it can never
          // inherit. The stored flags are treated as false there regardless of what was loaded.
          const fractionInherited = !isDefaultLevel && row.fractionInherited;
          const gameTagsInherited = !isDefaultLevel && row.gameTagsInherited;
          // When the fraction is inherited the input is read-only, so surface the value the level
          // actually resolves to rather than its own unused stored value.
          const displayFraction = fractionInherited ? inheritedFractionFor(row) : row.fraction;
          const gameTagsCollapsed = collapsedRows.has(row.rowId);
          // Which level to inherit from is only knowable once this row's level is set, so a new row
          // cannot stop inheriting game tags until then.
          const levelSpecified = row.level.trim() !== "" && !Number.isNaN(Number(row.level));
          return (
            <React.Fragment key={row.rowId}>
              {/* The header divider already separates the first row; the rest get their own, drawn heavier
                  to read as a break between levels rather than between fields. */}
              {index > 0 && <div className="hr-line-dashed hr-line-dashed--thick"></div>}
              <div
                className="mb-3"
                data-testid={`${row.rowId}-row`}
              >
                <div className="form-inline mb-2">
                  <label className="mr-2">{t("cashback.config-v2.level-label", undefined, "Level")}</label>
                  <input
                    type="number"
                    min="0"
                    className="form-control mr-3"
                    data-testid={`${row.rowId}-level`}
                    value={row.level}
                    onChange={(event) => updateRow(row.rowId, { level: event.target.value })}
                    placeholder={t("cashback.config-v2.level-placeholder", undefined, "e.g. 15")}
                    // Level 0 is the mandatory defaults row; keeping its level fixed stops it being renamed
                    // to a non-zero level, which would let it be removed and defeat the always-present guarantee.
                    readOnly={isDefaultLevel}
                  />
                  <label className="mr-2">{t("cashback.config-v2.fraction-label", undefined, "Fraction")}</label>
                  <input
                    type="text"
                    inputMode="decimal"
                    className="form-control mr-3"
                    data-testid={`${row.rowId}-fraction`}
                    value={displayFraction}
                    onChange={(event) => updateRow(row.rowId, { fraction: event.target.value.replace(",", ".") })}
                    placeholder={t("cashback.config-v2.fraction-placeholder", undefined, "e.g. 0.1")}
                    disabled={fractionInherited}
                  />
                  {/* Level 0 is the floor and cannot inherit, so the inherit controls only exist above it. */}
                  {!isDefaultLevel && (
                    <label className="checkbox-inline mr-3">
                      <input
                        type="checkbox"
                        className="mr-1"
                        data-testid={`${row.rowId}-fraction-inherited`}
                        checked={fractionInherited}
                        disabled={!levelSpecified}
                        title={levelSpecified ? undefined : t("cashback.config-v2.set-level-first", undefined, "Set the level first")}
                        onChange={(event) => updateRow(row.rowId, { fractionInherited: event.target.checked })}
                      />
                      {t("cashback.config-v2.fraction-inherited-label", undefined, "Inherit fraction")}
                    </label>
                  )}
                  {!isDefaultLevel && (
                    <label className="checkbox-inline mr-3">
                      <input
                        type="checkbox"
                        className="mr-1"
                        data-testid={`${row.rowId}-game-tags-inherited`}
                        checked={gameTagsInherited}
                        disabled={!levelSpecified}
                        title={levelSpecified ? undefined : t("cashback.config-v2.set-level-first", undefined, "Set the level first")}
                        onChange={(event) =>
                          event.target.checked
                            ? updateRow(row.rowId, { gameTagsInherited: true })
                            : updateRow(row.rowId, { gameTagsInherited: false, ...inheritedTagsFor(row) })
                        }
                      />
                      {t("cashback.config-v2.game-tags-inherited-label", undefined, "Inherit game tags")}
                    </label>
                  )}
                  {!isDefaultLevel && (
                    <button
                      type="button"
                      className="btn btn-sm btn-danger"
                      data-testid={`${row.rowId}-remove`}
                      onClick={() => removeRow(row.rowId)}
                      aria-label={t("cashback.config-v2.remove-override", undefined, "Remove override")}
                    >
                      &times;
                    </button>
                  )}
                  {thresholds && !fractionInherited && isLargeFraction(Number(row.fraction), basis, thresholds, rewardType) && (
                    <div
                      className="alert alert-warning w-100 mt-1 mb-0"
                      data-testid={`${row.rowId}-large-fraction-warning`}
                    >
                      <strong>{t("cashback.config-v2.large-fraction-warning-title", undefined, "Unusually large fraction")}</strong>{" "}
                      {t(
                        "cashback.config-v2.large-fraction-warning",
                        { threshold: largeFractionThreshold(basis, thresholds, rewardType), basis },
                        "A fraction of {{threshold}} or more is unusually large for {{basis}} and may pay out far more than intended. This is advisory only — you can still save.",
                      )}
                    </div>
                  )}
                  {!fractionInherited && blockedRowIds.includes(row.rowId) && (
                    <div
                      className="alert alert-danger w-100 mt-1 mb-0"
                      data-testid={`${row.rowId}-large-fraction-blocked`}
                    >
                      {blockedFractionMessage("cashback.config-v2.large-fraction-blocked", blockingFractionLimit(thresholds, rewardType))}
                    </div>
                  )}
                </div>

                {!gameTagsInherited && (
                  <div className="mb-2">
                    <button
                      type="button"
                      className="btn btn-sm btn-link p-0"
                      data-testid={`${row.rowId}-game-tags-toggle`}
                      aria-expanded={!gameTagsCollapsed}
                      onClick={() => toggleGameTagsCollapsed(row.rowId)}
                    >
                      {gameTagsCollapsed ? "▸" : "▾"} {t("cashback.config-v2.game-tags-toggle-label", undefined, "Game tags")}
                    </button>
                    {gameTagsCollapsed && (
                      <span
                        className="text-muted ml-2"
                        data-testid={`${row.rowId}-game-tags-summary`}
                      >
                        {t(
                          "cashback.config-v2.game-tags-summary",
                          { include: row.includeTags.length, exclude: row.excludeTags.length },
                          "{{include}} included / {{exclude}} excluded",
                        )}
                      </span>
                    )}
                  </div>
                )}

                {!gameTagsInherited && !gameTagsCollapsed && (
                  <div data-testid={`${row.rowId}-game-tags`}>
                    <GameTagPillSelector
                      label={t("cashback.config-v2.include-tags-label", undefined, "Include")}
                      testId={`${row.rowId}-include-tags`}
                      availableTags={availableTags}
                      selectedTags={row.includeTags}
                      onChange={(tags) => updateRow(row.rowId, { includeTags: tags })}
                      helpText={
                        <>
                          {t(
                            "cashback.config-v2.include-tags-help",
                            undefined,
                            "When any tags are set here, only games carrying at least one of these tags count toward this level.",
                          )}{" "}
                          <strong>{t("cashback.config-v2.include-tags-help-empty", undefined, "Leave empty to include every game.")}</strong>
                        </>
                      }
                    />
                    <GameTagPillSelector
                      label={t("cashback.config-v2.exclude-tags-label", undefined, "Exclude")}
                      testId={`${row.rowId}-exclude-tags`}
                      availableTags={availableTags}
                      selectedTags={row.excludeTags}
                      onChange={(tags) => updateRow(row.rowId, { excludeTags: tags })}
                      helpText={t(
                        "cashback.config-v2.exclude-tags-help",
                        undefined,
                        "Games carrying any of these tags will not count, even if they also match an Include tag.",
                      )}
                    />
                  </div>
                )}
              </div>
            </React.Fragment>
          );
        })}

        <div className="hr-line-dashed"></div>
        <button
          type="button"
          className="btn btn-sm btn-primary mt-2"
          data-testid="level-override-add"
          onClick={addRow}
        >
          {t("cashback.config-v2.add-override", undefined, "+ Add level")}
        </button>
      </div>
    </div>
  );
};
